// Pure helpers for the graph event popup (EventPopUp.jsx). React-free so they
// can be unit-tested with `node --test` (the test sets up the @managers alias
// and transpiles the shared module — see __tests__/registerManagersAlias.js).

// displayPressureUnit lives in sciroot-clients-shared's unitsManager so web and
// mobile render water-column pressure units (cm H₂O / inch H₂O) identically.
const { displayPressureUnit } = require('@managers/unitsManager');
// Same 'low' | 'high' normalization the reported-events badge uses, so the graph
// and the list agree on what counts as an alert.
const { pressureAlertLevel } = require('@utils/pressureAlert');
const dayjs = require('../../utils/dayjsConfig');

// Translation keys for the graph popup's alert line. Distinct from the bare
// `high_pressure_detected` label (used by ReportIssueModal) because this one
// interpolates the reading: "High pressure detected: 30 cm".
const PRESSURE_ALERT_VALUE_KEYS = {
  low: 'low_pressure_detected_value',
  high: 'high_pressure_detected_value',
};

// Shared text styles for the popup's measurement lines (Figma 12009:11615:
// Source Sans Pro SemiBold 14, Grey/71).
const POPUP_TEXT_STYLE =
  'font-size: 14px;white-space: nowrap;color: var(--color-grey-71)';
const POPUP_ALERT_TEXT_STYLE =
  'font-size: 14px;white-space: nowrap;color: var(--color-status-alert-96)';

// Formats the pressure subtitle text shown for an irrigation event:
// `value unit (Pressure)`, with a space between the value and unit (matching
// the reported-events subtitle). Shared by the graph popup (buildPressureRow)
// and the reported-events list (NextActionsContainer) so both read identically.
//
// Returns null when there is no pressure reading, so non-pressure irrigation
// events are left unchanged. A pressure of 0 is a valid reading and is shown
// (only null/undefined are treated as "no reading").
const formatPressureSubtitle = (pressure, pressureUnit, pressureLabel) => {
  if (pressure === null || pressure === undefined) {
    return null;
  }
  let unitSuffix = pressureUnit ? ' ' + displayPressureUnit(pressureUnit) : '';
  return pressure + unitSuffix + ' (' + pressureLabel + ')';
};

// Builds the pressure line shown under the irrigation amount in an irrigation
// event popup. The text mirrors the reported-events subtitle in
// NextActionsContainer (`value unit (Pressure)`), and the markup matches the
// popup's first line (irrigation amount) so the two lines look identical.
//
// Returns '' when there is no pressure reading, so non-pressure irrigation
// events render exactly as before.
const buildPressureRow = (pressure, pressureUnit, pressureLabel) => {
  const text = formatPressureSubtitle(pressure, pressureUnit, pressureLabel);
  if (text === null) {
    return '';
  }
  return (
    "<div class='pl-2 mr-1 text-grey71 font-weight-semi-bold align-self-center' style='font-size: 14px;white-space: nowrap'>" +
    text +
    '</div>'
  );
};

// `30 cm` — the reading as the graph popup prints it. The redesign (Figma
// 12009:11615) spells the unit out verbatim rather than through
// displayPressureUnit, so the graph reads "30 cm" where the reported-events
// list still reads "30 cmH₂O". That divergence is intentional and product-
// approved; formatPressureSubtitle above is deliberately left untouched.
//
// Returns null when there is no reading. A pressure of 0 is a valid reading.
const formatGraphPressureValue = (pressure, pressureUnit) => {
  if (pressure === null || pressure === undefined) {
    return null;
  }
  return pressureUnit ? pressure + ' ' + pressureUnit : String(pressure);
};

// `Pressure: 30 cm` — the second line of the popup's measurement block.
const formatGraphPressureText = (pressure, pressureUnit, pressureLabel) => {
  const value = formatGraphPressureValue(pressure, pressureUnit);
  return value === null ? null : pressureLabel + ': ' + value;
};

// `High pressure detected: 30 cm` — the red third line, shown only when the API
// flagged this irrigation's pressure as out of bounds. Returns null when there
// is no alert or no reading to quote, so the line is simply omitted.
const formatPressureAlertText = (alertLevel, pressure, pressureUnit, t) => {
  const level = pressureAlertLevel(alertLevel);
  const value = formatGraphPressureValue(pressure, pressureUnit);
  if (!level || value === null) {
    return null;
  }
  return t(PRESSURE_ALERT_VALUE_KEYS[level], { value: value });
};

// The alert line's markup: a 12x12 triangle nudged down 4px so it centers on
// the cap height of the 14px text beside it (Figma 12009:11629).
const buildPressureAlertRow = (text, alertIconSrc) => {
  if (!text) {
    return '';
  }
  return (
    "<div style='display: flex;gap: 2px;align-items: center'>" +
    "<div style='display: flex;align-items: flex-start;align-self: stretch;padding-top: 4px'>" +
    "<img style='width: 12px;height: 12px' src='" +
    alertIconSrc +
    "'/>" +
    '</div>' +
    "<div class='font-weight-semi-bold' style='" +
    POPUP_ALERT_TEXT_STYLE +
    "'>" +
    text +
    '</div>' +
    '</div>'
  );
};

// The pressure line for the graph popup's measurement block: the red alert
// line when the API flagged the reading, otherwise the grey "Pressure: X" line.
// The alert replaces the reading rather than sitting under it, because it
// already quotes the value (Figma 12009:11615; mobile pressureFormat.js does
// the same). Returns '' for irrigation events with no pressure sensor, so those
// popups are unchanged.
const buildGraphPressureRows = (options) => {
  const { pressure, pressureUnit, pressureLabel, alertLevel, alertIconSrc, t } =
    options;
  const alertText = formatPressureAlertText(
    alertLevel,
    pressure,
    pressureUnit,
    t,
  );
  if (alertText !== null) {
    return buildPressureAlertRow(alertText, alertIconSrc);
  }
  const readingText = formatGraphPressureText(
    pressure,
    pressureUnit,
    pressureLabel,
  );
  if (readingText === null) {
    return '';
  }
  return (
    "<div class='font-weight-semi-bold' style='" +
    POPUP_TEXT_STYLE +
    "'>" +
    readingText +
    '</div>'
  );
};

// `{ date: '9 JUN', time: '8:30 am' }` — one end of the redesigned card's time
// frame. The legacy popup keeps its own `MAY 31` / `09:45 pm` format. With no
// timezone (the planner) the instant is formatted in local time.
const formatCardDateTime = (instant, timezone) => {
  const value = timezone ? dayjs(instant).tz(timezone) : dayjs(instant);
  return {
    date: value.format('D MMM').toUpperCase(),
    time: value.format('h:mm a'),
  };
};

module.exports = {
  buildPressureRow,
  formatPressureSubtitle,
  displayPressureUnit,
  formatGraphPressureValue,
  formatGraphPressureText,
  formatPressureAlertText,
  buildPressureAlertRow,
  buildGraphPressureRows,
  formatCardDateTime,
  POPUP_TEXT_STYLE,
  POPUP_ALERT_TEXT_STYLE,
};
